import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { mergeRefs } from 'react-merge-refs';

import { useVirtualizer } from '@tanstack/react-virtual';

import useIgnorableError from '@ecp/common/src/hooks/useIgnorableError';
import boTheme from '@ecp/common/src/style/theme/bo';
import { boScrollCss } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import Input, { inputBasePropTypes } from '@ecp/common/src/components/input/bo/Input';
import { ReactComponent as SearchSvg } from '@ecp/common/src/assets/icon/icon__search--grey.svg';

import ResultItemList from './ResultItemList';
import useAutocomplete from './useAutocomplete';

const Container = styled(FlexBoxColumn)`
  width: ${({ width }) => width};
  height: ${({ height }) => height};
`;

const openInputStyle = css`
  ${({ floatingDirection }) => `border-${floatingDirection}-left-radius: 0`};
  ${({ floatingDirection }) => `border-${floatingDirection}-right-radius: 0`};
  ${({ floatingDirection }) => `border-${floatingDirection}: none !important`};
`;

const InputWrapper = styled(FlexBox)`
  width: 100%;
  height: fit-content;

  & > div > div {
    ${({ open }) => open && openInputStyle}
  }
`;

const SuggestItemsWrapper = styled(FlexBox)`
  display: block;
  position: absolute;
  top: 28px;
  left: 0;
  min-height: 32px;
  max-height: 275px;
  width: 100%;
  ${({ floatingDirection }) => `border-${floatingDirection}-left-radius: 3px`};
  ${({ floatingDirection }) => `border-${floatingDirection}-right-radius: 3px`};
  z-index: 3;
  background-color: ${boTheme.color.sub10};
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid ${boTheme.color.main};
  ${({ floatingDirection }) => `border-${floatingDirection === 'top' ? 'bottom' : 'top'}: none`};

  ${boScrollCss};
`;

const Autocomplete = React.forwardRef((props, ref) => {
  const {
    value,
    index,
    endAdornment,
    searchCondition,
    items,
    handleSearchClick,
    width,
    height,
    disabled,
    readOnly,
    onChange,
    error,
  } = props;

  const { ignoreError } = useIgnorableError({ error, value });
  const { open, setOpen, refs, getReferenceProps, floatingPosition, positionStrategy, onItemClick } =
    useAutocomplete(props);

  const refinedSearchKeyword = useMemo(() => value?.replaceAll(/[ㄱ-ㅎㅏ-ㅣ]/g, ''), [value]);
  const [fixedValue, setFixedValue] = useState(refinedSearchKeyword || '');

  useEffect(() => {
    if (searchCondition(refinedSearchKeyword) || refinedSearchKeyword.length === 0) {
      setFixedValue(refinedSearchKeyword);
    }
  }, [value, refinedSearchKeyword, searchCondition]);

  const searchResult = useMemo(
    () =>
      fixedValue
        ? items?.filter(({ itemLabel = '' }) => itemLabel.toLowerCase().includes(fixedValue.toLowerCase()))
        : [],
    [fixedValue, items]
  );

  const showAutocompleteList = useMemo(
    () => !disabled && !readOnly && value?.length > 0 && open,
    [disabled, readOnly, value?.length, open]
  );

  const targetRef = useMemo(() => mergeRefs([refs.setReference, ref]), [refs.setReference, ref]);
  const floatingDirection = useMemo(() => (floatingPosition?.top < 0 ? 'top' : 'bottom'), [floatingPosition?.top]);

  const virtualizer = useVirtualizer({
    count: searchResult.length,
    getScrollElement: () => refs.floating.current,
    estimateSize: () => 30,
    overscan: 5,
  });

  const handleSearch = useCallback(
    (e) => {
      if (disabled || readOnly) return;
      e.stopPropagation();
      handleSearchClick?.(searchResult);
      setOpen(false);
    },
    [disabled, handleSearchClick, readOnly, searchResult, setOpen]
  );

  const showList = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleInputChange = useCallback(
    (e) => {
      showList();
      onChange?.(e, index);
    },
    [onChange, showList, index]
  );

  const handleMouseDown = (e) => {
    e.preventDefault();
    handleSearch(e);
  };

  return (
    <Container width={width} height={height}>
      <InputWrapper
        {...getReferenceProps({ ref: targetRef })}
        open={showAutocompleteList}
        floatingDirection={floatingDirection}
      >
        <Input
          {...props}
          ref={ref}
          height={'28px'}
          onChange={handleInputChange}
          value={value}
          onFocus={showList}
          onEnter={handleSearch}
          error={!ignoreError && !showAutocompleteList && error}
          disableIgnoringError
          endAdornment={
            <>
              <SearchIcon onMouseDown={handleMouseDown} />
              {endAdornment}
            </>
          }
        />
      </InputWrapper>
      {showAutocompleteList && virtualizer && (
        <SuggestItemsWrapper
          ref={refs.setFloating}
          floatingDirection={floatingDirection}
          style={{ position: positionStrategy, top: floatingPosition.top, left: floatingPosition.left }}
        >
          <ResultItemList
            items={searchResult}
            keyword={fixedValue}
            virtualizer={virtualizer}
            handleItemClick={onItemClick}
            handleEnter={handleSearch}
          />
        </SuggestItemsWrapper>
      )}
    </Container>
  );
});

Autocomplete.propTypes = {
  searchCondition: PropTypes.func,
  items: PropTypes.array,
  index: PropTypes.number,
  handleItemClick: PropTypes.func,
  handleSearchClick: PropTypes.func,
  setValue: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  ...inputBasePropTypes,
};

Autocomplete.defaultProps = {
  placement: 'bottom-start',
  searchCondition: () => true,
  value: '',
  width: '100%',
  height: '28px',
};

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;

const SearchIcon = styled(SearchSvg)`
  cursor: pointer;
  width: 14px;
  height: 14px;
`;
